import PropTypes from "prop-types";
import { useState, useRef, useEffect } from "react";
import { Modal, Checkbox, Button, Typography, Divider, Space, Tag } from "antd";
import { SafetyOutlined, FileProtectOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;

const P  = "#2596be";
const P_PALE = "#e0f4fb";

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 20 }}>
    <Text strong style={{ fontSize: 13, color: P, display: "block", marginBottom: 6 }}>
      {title}
    </Text>
    <Paragraph
      style={{ fontSize: 13, color: "#4a4a4a", lineHeight: 1.7, marginBottom: 0 }}
    >
      {children}
    </Paragraph>
  </div>
);

/**
 * Modal de Políticas de Privacidad — no puede cerrarse sin aceptar.
 *
 * Props:
 *   open        {boolean}   — controla visibilidad
 *   onAceptar   {() => void} — callback cuando el usuario acepta y confirma
 */
const ModalTerminos = ({ open, onAceptar }) => {
  const [checked,   setChecked]   = useState(false);
  const [scrolled,  setScrolled]  = useState(false);
  const bodyRef = useRef(null);

  // Resetear estado cada vez que el modal se abre
  useEffect(() => {
    if (open) {
      setChecked(false);
      setScrolled(false);
    }
  }, [open]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    // Se considera "leído" cuando el usuario llega al 85 % del contenido
    if (scrollTop + clientHeight >= scrollHeight * 0.85) {
      setScrolled(true);
    }
  };

  const handleAceptar = () => {
    if (!checked) return;
    onAceptar();
  };

  return (
    <Modal
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      width={680}
      centered
      styles={{
        content: { borderRadius: 16, padding: 0, overflow: "hidden" },
        mask:    { backdropFilter: "blur(4px)" },
      }}
    >
      {/* ── Encabezado ───────────────────────────────────── */}
      <div
        style={{
          background:    `linear-gradient(135deg, ${P} 0%, #1a7a9e 100%)`,
          padding:       "24px 28px 20px",
          display:       "flex",
          alignItems:    "center",
          gap:           12,
        }}
      >
        <SafetyOutlined style={{ fontSize: 28, color: "#fff" }} />
        <div>
          <Title level={4} style={{ color: "#fff", margin: 0, lineHeight: 1.2 }}>
            Políticas de Privacidad
          </Title>
          <Text style={{ color: "rgba(255,255,255,0.80)", fontSize: 12 }}>
            ARS Renacer — debe aceptar para continuar usando la plataforma
          </Text>
        </div>
        <Tag
          icon={<FileProtectOutlined />}
          color="geekblue"
          style={{ marginLeft: "auto", borderRadius: 20, fontSize: 11 }}
        >
          v1.0
        </Tag>
      </div>

      {/* ── Cuerpo desplazable ───────────────────────────── */}
      <div
        ref={bodyRef}
        onScroll={handleScroll}
        style={{
          maxHeight:    420,
          overflowY:    "auto",
          padding:      "24px 28px 8px",
          background:   "#fafbfc",
        }}
      >
        <Section title="Políticas de Privacidad">
          La presente Política de Privacidad establece los términos en que ARS Renacer
          usa y protege la información que es proporcionada por sus usuarios al momento
          de utilizar su sitio web y su app. Esta compañía está comprometida con la
          seguridad de los datos de sus usuarios. Cuando le pedimos llenar los campos de
          información personal con la cual usted pueda ser identificado, lo hacemos
          asegurando que sólo se empleará de acuerdo con los términos de este documento.
          Sin embargo esta Política de Privacidad puede cambiar con el tiempo o ser
          actualizada por lo que le recomendamos y enfatizamos revisar continuamente esta
          página para asegurarse que está de acuerdo con dichos cambios.
        </Section>

        <Divider style={{ margin: "0 0 20px" }} />

        <Section title="Información recogida">
          Nuestro sitio web y nuestra app podrán recoger información personal, por
          ejemplo: Nombre, información de contacto como su dirección de correo
          electrónica e información demográfica. Así mismo cuando sea necesario podrá
          ser requerida información específica para procesar algún pedido o realizar una
          entrega o facturación.
        </Section>

        <Divider style={{ margin: "0 0 20px" }} />

        <Section title="Uso de la información recogida">
          Nuestro sitio web y nuestra app emplean la información con el fin de
          proporcionar el mejor servicio posible, particularmente para mantener un
          registro de usuarios, de pedidos en caso que aplique, y mejorar nuestros
          productos y servicios. Es posible que sean enviados correos electrónicos
          periódicamente a través de nuestro sitio con ofertas especiales, nuevos
          productos y otra información publicitaria que consideremos relevante para usted
          o que pueda brindarle algún beneficio, estos correos electrónicos serán
          enviados a la dirección que usted proporcione y podrán ser cancelados en
          cualquier momento.
          {"\n\n"}
          ARS Renacer está altamente comprometido para cumplir con el compromiso de
          mantener su información segura. Usamos los sistemas más avanzados y los
          actualizamos constantemente para asegurarnos que no exista ningún acceso no
          autorizado.
        </Section>

        <Divider style={{ margin: "0 0 20px" }} />

        <Section title="Cookies">
          Una cookie se refiere a un fichero que es enviado con la finalidad de
          solicitar permiso para almacenarse en su ordenador, al aceptar dicho fichero
          se crea y la cookie sirve entonces para tener información respecto al tráfico
          web, y también facilita las futuras visitas a una web recurrente. Otra función
          que tienen las cookies es que con ellas las web pueden reconocerte
          individualmente y por tanto brindarte el mejor servicio personalizado de su
          web.
          {"\n\n"}
          Nuestro sitio web y nuestra app emplean las cookies para poder identificar las
          páginas que son visitadas y su frecuencia. Esta información es empleada
          únicamente para análisis estadístico y después la información se elimina de
          forma permanente. Usted puede eliminar las cookies en cualquier momento desde
          su ordenador. Sin embargo las cookies ayudan a proporcionar un mejor servicio
          de los sitios web, estás no dan acceso a información de su ordenador ni de
          usted, a menos de que usted así lo quiera y la proporcione directamente. Usted
          puede aceptar o negar el uso de cookies, sin embargo la mayoría de navegadores
          aceptan cookies automáticamente pues sirve para tener un mejor servicio web.
          También usted puede cambiar la configuración de su ordenador para declinar las
          cookies. Si se declinan es posible que no pueda utilizar algunos de nuestros
          servicios.
        </Section>

        <Divider style={{ margin: "0 0 20px" }} />

        <Section title="Enlaces a Terceros">
          Este sitio web pudiera contener enlaces a otros sitios que pudieran ser de su
          interés. Una vez que usted de clic en estos enlaces y abandone nuestra página,
          ya no tenemos control sobre al sitio al que es redirigido y por lo tanto no
          somos responsables de los términos o privacidad ni de la protección de sus
          datos en esos otros sitios terceros. Dichos sitios están sujetos a sus propias
          políticas de privacidad por lo cual es recomendable que los consulte para
          confirmar que usted está de acuerdo con estas.
        </Section>

        <Divider style={{ margin: "0 0 20px" }} />

        <Section title="Control de su información personal">
          En cualquier momento usted puede restringir la recopilación o el uso de la
          información personal que es proporcionada a nuestro sitio web y nuestra app.
          Cada vez que se le solicite rellenar un formulario, como el de alta de usuario,
          puede marcar o desmarcar la opción de recibir información por correo
          electrónico. En caso de que haya marcado la opción de recibir nuestro boletín
          o publicidad usted puede cancelarla en cualquier momento.
          {"\n\n"}
          Esta compañía no venderá, cederá ni distribuirá la información personal que es
          recopilada sin su consentimiento, salvo que sea requerido por un juez con un
          orden judicial.
          {"\n\n"}
          ARS Renacer se reserva el derecho de cambiar los términos de la presente
          Política de Privacidad en cualquier momento.
        </Section>

        {/* Espaciado extra al final para dar aire antes del pie */}
        <div style={{ height: 8 }} />
      </div>

      {/* ── Nota de desplazamiento (visible mientras no ha scrolleado) ─── */}
      {!scrolled && (
        <div
          style={{
            background:  P_PALE,
            borderLeft:  `3px solid ${P}`,
            padding:     "8px 28px",
            fontSize:    12,
            color:       "#1a7a9e",
          }}
        >
          ↓ Desplace hacia abajo para leer el documento completo antes de aceptar.
        </div>
      )}

      {/* ── Pie ─────────────────────────────────────────────────────────── */}
      <div
        style={{
          padding:       "16px 28px 20px",
          background:    "#fff",
          borderTop:     "1px solid #f0f0f0",
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size={14}>
          <Checkbox
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
            style={{ fontSize: 13 }}
          >
            He leído y acepto las{" "}
            <Text strong style={{ color: P }}>
              Políticas de Privacidad
            </Text>{" "}
            de ARS Renacer.
          </Checkbox>

          <Button
            type="primary"
            size="large"
            block
            disabled={!checked}
            onClick={handleAceptar}
            style={{
              background:    checked ? P : undefined,
              borderColor:   checked ? P : undefined,
              borderRadius:  10,
              fontWeight:    600,
              height:        44,
            }}
          >
            Aceptar y continuar
          </Button>
        </Space>
      </div>
    </Modal>
  );
};

ModalTerminos.propTypes = {
  open: PropTypes.bool.isRequired,
  onAceptar: PropTypes.func.isRequired,
};

export default ModalTerminos;
